import moment from 'moment-timezone';
import React from 'react';
import { useSelector } from 'react-redux';
import { useListChargePointSessions } from '../../../../../app/ApiGen';
import { RefreshButton } from '../../../../../components/DataTable/Toolbar';
import { DateRangePicker } from '../../../../../components/DateRange';
import { DataErrorHandler } from '../../../../../components/ErrorHandler';
import { ExportCsv, type ExportProps } from '../../../../../components/ExportCsv';
import { OrganisationId } from '../../../../../config';
import {
  Card,
  ColumnChanger,
  type ColumnChangerProps,
  DataTable,
  SearchField,
  SearchFieldProps,
  Section,
  Workspace,
} from '../../../../../design-system';
import type { ApplicationState } from '../../../../../store/types';
import { DateFormat } from '../../../../../utils/dateFormat';
import { useEndDateState, useSetDateRange, useStartDateState } from '../../../../../utils/dateRange';
import { normalise } from '../../../../../utils/request';
import { includesSearch } from '../../../../../utils/search';
import { type TableSessionModel, toTableSessionModel } from '../../../../../utils/sessions';
import {
  carbonOffsetColumn,
  connectorIdColumn,
  costColumn,
  evseIdColumn,
  reimbursementFigureColumn,
  sessionIdColumn,
  stopReasonColumn,
  tokenNameColumn,
  tokenUidColumn,
} from '../../../../../utils/sessions/columns/creator';
import {
  dateFormat,
  durationFormatFromStartEnd,
  powerUsageWithUnit,
} from '../../../../../utils/sessions/columns/format';
import { renderDate, renderTotalCarbonUsage } from '../../../../../utils/sessions/columns/render';
import { searchTotalCarbonUsage } from '../../../../../utils/sessions/columns/search';
import { sortByDate, sortByDuration, sortByValue } from '../../../../../utils/sortBy';
import { csvDateFormat } from '../../Reports/utils';
import { useChargePoint } from './Provider';

const DEFAULT_SESSION_DAYS_AGO = 30;

const dateIncludesSearch = (date: string, searchValue: unknown): boolean =>
  includesSearch(dateFormat(date), searchValue);

const SessionsView: React.FC = () => {
  const { chargePoint } = useChargePoint();
  const currentOrganisation = useSelector((state: ApplicationState) => state.organisation.current);
  const isAsbOrganisation = currentOrganisation?.id === OrganisationId.Asb;

  const [fromDate, setFromDate] = useStartDateState(DEFAULT_SESSION_DAYS_AGO);
  const [toDate, setToDate] = useEndDateState();
  const setDateRange = useSetDateRange(setFromDate, setToDate);

  const chargePointId: string = chargePoint?.id ?? '';

  const fromFormatted = moment(fromDate).format(DateFormat.Filename);
  const toFormatted = moment(toDate).format(DateFormat.Filename);
  const exportFileName = `sessions-${
    chargePoint?.referenceId ?? chargePointId
  }-from-${fromFormatted}-to-${toFormatted}`;

  const { data, error, refetch, loading } = useListChargePointSessions({
    id: chargePointId,
    queryParams: { from: fromDate.toISOString(), to: toDate.toISOString() },
  });
  const tableData: TableSessionModel[] = data
    ? normalise(data).map((session) => toTableSessionModel({ session, forAsb: isAsbOrganisation }))
    : [];

  if (error) {
    return <DataErrorHandler error={error} refetch={refetch} description="Unable to load charge point sessions" />;
  }

  return (
    <Workspace maxWidth="xl">
      <Section title="Charge point sessions" description="View records of sessions at this charge point.">
        <Card>
          <DataTable<TableSessionModel>
            isLoading={loading}
            options={{ columnsButton: true }}
            toolbarProps={{
              search: ({ dataManager, search, onSearchChanged }: SearchFieldProps) => (
                <span style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ paddingBottom: 8 }}>
                    <DateRangePicker fromDate={fromDate} toDate={toDate} onApply={setDateRange} />
                  </span>
                  <SearchField
                    dataManager={dataManager}
                    item="sessions"
                    onSearchChanged={onSearchChanged}
                    search={search}
                  />
                </span>
              ),
              actions: (props: ColumnChangerProps & ExportProps<TableSessionModel>) => (
                <>
                  <ColumnChanger
                    columnsButton={props.columnsButton}
                    columns={props.columns}
                    icons={props.icons}
                    onColumnsChanged={props.onColumnsChanged}
                  />
                  <RefreshButton refetch={refetch} />
                  <ExportCsv<TableSessionModel>
                    columns={props.columns}
                    data={props.data}
                    dateFormat={csvDateFormat}
                    exportFileName={exportFileName}
                    getFieldValue={props.getFieldValue}
                    icons={props.icons}
                  />
                </>
              ),
            }}
            columns={[
              sessionIdColumn(),
              {
                field: 'startDate',
                title: 'Start time',
                type: 'date',
                defaultSort: 'desc',
                customSort: sortByDate('startDate'),
                render: ({ startDate }: TableSessionModel) => renderDate(startDate),
                customFilterAndSearch: (searchValue: unknown, { startDate }) =>
                  dateIncludesSearch(startDate, searchValue),
              },
              {
                field: 'endDate',
                title: 'End time',
                type: 'date',
                customSort: sortByDate('endDate'),
                render: ({ endDate }: TableSessionModel) => renderDate(endDate),
                customFilterAndSearch: (searchValue: unknown, { endDate }) =>
                  !!endDate && dateIncludesSearch(endDate, searchValue),
              },
              evseIdColumn(),
              connectorIdColumn(),
              tokenUidColumn(),
              { ...tokenNameColumn(), hidden: true },
              {
                title: 'Duration',
                field: 'duration',
                customSort: sortByDuration('startDate', 'endDate'),
                render: ({ startDate, endDate }: TableSessionModel) =>
                  startDate && endDate && <>{durationFormatFromStartEnd(startDate, endDate)}</>,
                customFilterAndSearch: (searchValue: unknown, { startDate, endDate }) =>
                  !!startDate &&
                  !!endDate &&
                  includesSearch(durationFormatFromStartEnd(startDate, endDate), searchValue),
              },
              stopReasonColumn(),
              {
                field: 'totalCarbonUsage',
                title: 'Carbon use',
                customSort: sortByValue('totalCarbonUsage'),
                render: renderTotalCarbonUsage,
                customFilterAndSearch: searchTotalCarbonUsage,
              },
              { ...carbonOffsetColumn(), hidden: true },
              {
                title: 'Energy delivered',
                field: 'usage',
                customSort: sortByValue('usage'),
                render: ({ usage }) => usage !== undefined && <>{powerUsageWithUnit(usage)}</>,
                customFilterAndSearch: (searchValue: unknown, { usage }) =>
                  usage !== undefined && includesSearch(powerUsageWithUnit(usage), searchValue),
              },
              costColumn(),
              {
                field: 'transactionId',
                title: 'Transaction ID',
                customSort: sortByValue('transactionId'),
                hidden: true,
              },
              reimbursementFigureColumn({ isDisplayed: isAsbOrganisation }),
            ]}
            data={tableData}
          />
        </Card>
      </Section>
    </Workspace>
  );
};

export default SessionsView;
